// UserSettings.ts - 用户设置管理类

// 导入AI配置数据和类型
import { AIProviderType } from '../services/ai/AIServiceFactory';
import { getDefaultConfig } from '../services/ai/configs';
import { BaseConfig } from '../services/ai/types';

// 主题类型
export type ThemeType = 'light' | 'dark' | 'system';

// 语言类型
export type LanguageType = 'zh' | 'en';

// 用户设置接口
export interface UserSettingsData {
  theme: ThemeType;
  language: LanguageType;
  aiProvider: AIProviderType;
  aiConfigs: Record<string, BaseConfig>;
  // 将来可以在这里添加更多设置项
}

// 获取默认AI配置
function getDefaultAIConfigs(): Record<string, BaseConfig> {
  return {
    openai: getDefaultConfig('openai') || {useStreaming: true},
    azureopenai: getDefaultConfig('azureopenai') || {useStreaming: true},
    claude: getDefaultConfig('claude') || {useStreaming: true},
    ollama: getDefaultConfig('ollama') || {useStreaming: true},
    vllm: getDefaultConfig('vllm') || {useStreaming: true},
    lmstudio: getDefaultConfig('lmstudio') || {useStreaming: true}
  };
}

// 默认设置
const DEFAULT_SETTINGS: UserSettingsData = {
  theme: 'system',
  language: 'zh',
  aiProvider: null,
  aiConfigs: getDefaultAIConfigs()
};

// 本地存储的键名
const STORAGE_KEY = 'ai_editor_user_settings';

class UserSettings {
  private settings: UserSettingsData;
  private systemDarkMode: boolean;
  private listeners: Set<() => void> = new Set();

  constructor() {
    // 加载保存的设置或使用默认设置
    this.settings = this.loadSettings();
    
    // 检查系统是否为深色模式
    this.systemDarkMode = window.matchMedia('(prefers-color-scheme: dark)').matches;
    
    // 监听系统主题变化
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (e) => {
      this.systemDarkMode = e.matches;
      this.applyTheme();
      this.notifyListeners();
    });
    
    // 初始应用主题
    this.applyTheme();
  }

  // 加载设置
  private loadSettings(): UserSettingsData {
    try {
      const savedSettings = localStorage.getItem(STORAGE_KEY);
      if (savedSettings) {
        return { ...DEFAULT_SETTINGS, ...JSON.parse(savedSettings) };
      }
    } catch (e) {
      console.error('Failed to load settings from localStorage:', e);
    }
    return { ...DEFAULT_SETTINGS };
  }

  // 保存设置
  private saveSettings(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.settings));
    } catch (e) {
      console.error('Failed to save settings to localStorage:', e);
    }
  }

  // 获取所有设置
  getSettings(): UserSettingsData {
    return { ...this.settings };
  }

  // 更新设置
  updateSettings(newSettings: Partial<UserSettingsData>): void {
    this.settings = { ...this.settings, ...newSettings };
    this.saveSettings();
    this.applyTheme();
    this.notifyListeners();
  }

  // 获取主题类型
  getTheme(): ThemeType {
    return this.settings.theme;
  }

  // 获取实际应用的主题（考虑系统主题）
  getEffectiveTheme(): 'light' | 'dark' {
    if (this.settings.theme === 'system') {
      return this.systemDarkMode ? 'dark' : 'light';
    }
    return this.settings.theme;
  }

  // 设置主题
  setTheme(theme: ThemeType): void {
    this.settings.theme = theme;
    this.saveSettings();
    this.applyTheme();
    this.notifyListeners();
  }

  // 应用主题到文档
  private applyTheme(): void {
    const effectiveTheme = this.getEffectiveTheme();
    document.documentElement.classList.remove('light', 'dark');
    document.documentElement.classList.add(effectiveTheme);
  }

  // 获取语言
  getLanguage(): LanguageType {
    return this.settings.language;
  }

  // 设置语言
  setLanguage(language: LanguageType): void {
    this.settings.language = language;
    this.saveSettings();
    this.notifyListeners();
  }

  // 获取当前AI提供商
  getAIProvider(): AIProviderType {
    return this.settings.aiProvider;
  }

  // 设置AI提供商
  setAIProvider(provider: AIProviderType): void {
    this.settings.aiProvider = provider;
    this.saveSettings();
    this.notifyListeners();
  }

  // 获取指定AI提供商的配置
  getAIConfig(provider: string): BaseConfig | null {
    return this.settings.aiConfigs[provider] || null;
  }

  // 更新指定AI提供商的配置
  updateAIConfig(provider: string, config: Partial<BaseConfig>): void {
    if (!provider) return;
    this.settings.aiConfigs[provider] = {
      ...this.settings.aiConfigs[provider],
      ...config
    };
    this.saveSettings();
    this.notifyListeners();
  }

  // 为扩展兼容性添加的通用获取/设置首选项方法
  getUserPreference<T>(key: keyof UserSettingsData): T {
    return this.settings[key] as T;
  }

  setUserPreference<T>(key: keyof UserSettingsData, value: T): void {
    if (key in this.settings) {
      (this.settings[key] as unknown) = value;
      this.saveSettings();
      this.notifyListeners();
    }
  }

  // 添加监听器
  addListener(callback: () => void): () => void {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  // 通知所有监听器
  private notifyListeners(): void {
    this.listeners.forEach(listener => listener());
  }
}

// 创建单例实例并导出
const userSettings = new UserSettings();
export default userSettings; 